import axios from 'axios';

const client = axios.create({ baseURL: import.meta.env.VITE_API_BASE_URL || '' });
const TOKEN_KEY = 'medease.portal.token';
const SESSION_KEY = 'medease.portal.session';

const read = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k: string, v: string | null) => {
  try {
    if (v == null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    /* private mode: works for this visit only */
  }
};

export interface PortalData {
  locked: boolean;
  unlockWith?: 'year' | 'mobile';
  clinic: { name?: string; phone?: string; bookingPath?: string };
  patient?: { firstName: string; lastName: string; abha?: string };
  appointments?: { appointmentDate: string; appointmentTime: string; doctorName: string; status: string }[];
  prescriptions?: { id: string; prescriptionNumber: string; visitDate: string; doctorName?: string; diagnosis?: string; medicines: number }[];
  labReports?: { id: string; reportNumber: string; testType: string; sampleCollectedAt: string }[];
}

let memorySession: string | null = null;

export const portalService = {
  /** The link this device last opened (for the installed app). */
  savedToken: () => read(TOKEN_KEY),
  remember: (token: string) => {
    if (read(TOKEN_KEY) !== token) write(SESSION_KEY, null);
    write(TOKEN_KEY, token);
  },
  forget: () => {
    write(TOKEN_KEY, null);
    write(SESSION_KEY, null);
    memorySession = null;
  },
  get: async (token: string): Promise<PortalData> => {
    const session = memorySession ?? read(SESSION_KEY);
    return (await client.get<PortalData>(`/api/public/my/${token}`, { headers: session ? { 'X-Portal-Session': session } : {} })).data;
  },
  unlock: async (token: string, answer: string) => {
    const { session } = (await client.post<{ session: string }>(`/api/public/my/${token}/unlock`, { answer })).data;
    memorySession = session;
    write(SESSION_KEY, session);
  },
  pdf: async (token: string, kind: 'prescriptions' | 'lab-reports', id: string): Promise<string> => {
    const session = memorySession ?? read(SESSION_KEY);
    return (await client.get<{ url: string }>(`/api/public/my/${token}/${kind}/${id}/pdf`, { headers: session ? { 'X-Portal-Session': session } : {} })).data.url;
  },
};
